import { useState, useEffect, useRef } from "react";
import { openPaywall } from "../components/Paywall";
import { useLanguage } from "../contexts/LanguageContext";
import { compressImage, uploadWithProgress } from "../lib/imageUpload";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { ArrowLeft, Send, Heart, Star, BadgeCheck, AlertCircle, Image, Mic, MicOff, X, Play, Pause, Lock, Unlock, Coins, MessageCircle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";

import { API } from "../config";
import { tx } from "../lib/tx";

export default function Chat() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const { language } = useLanguage();
  const en = language === "en";
  const [otherUser, setOtherUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isMatch, setIsMatch] = useState(false);
  const [messageStatus, setMessageStatus] = useState({ free_messages_remaining: 3 });
  // Solde du header toujours synchronisé avec les dépenses (messages, photos, déblocages)
  useEffect(() => {
    if (typeof messageStatus.credits !== "number") return;
    setUser?.((prev) => (prev && prev.credits !== messageStatus.credits ? { ...prev, credits: messageStatus.credits } : prev));
  }, [messageStatus.credits, setUser]);
  const [isConversationUnlocked, setIsConversationUnlocked] = useState(true);
  const [isFakeMember, setIsFakeMember] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Media states
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [playingAudio, setPlayingAudio] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  const fileInputRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    fetchOtherUser();
    fetchMessages();
    fetchMessageStatus();
    checkIfMatch();
    
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [userId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchOtherUser = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/users/${userId}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setOtherUser(data);
      }
    } catch (error) {
      console.error("Fetch user error:", error);
    }
  };

  const checkIfMatch = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/matches`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const matches = await response.json();
        const matched = matches.some(m => m.user.user_id === userId);
        setIsMatch(matched);
      }
    } catch (error) {
      console.error("Check match error:", error);
    }
  };

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/messages/${userId}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Gérer le nouveau format avec messages floutés
        if (data.messages) {
          setMessages(data.messages);
          setIsConversationUnlocked(data.is_conversation_unlocked);
          setIsFakeMember(data.is_fake_member);
        } else {
          // Ancien format (tableau simple)
          setMessages(data);
        }
      }
    } catch (error) {
      console.error("Fetch messages error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Débloquer la conversation avec un faux membre
  const handleUnlockConversation = async () => {
    setUnlocking(true);
    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/conversations/unlock/${userId}`, {
        method: "POST",
        headers: { 
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setIsConversationUnlocked(true);
        setMessageStatus(prev => ({
          ...prev,
          free_messages_remaining: data.free_messages_remaining,
          credits: data.credits
        }));
        // Recharger les messages pour voir le contenu débloqué
        await fetchMessages();
      } else if (response.status !== 402) {
        const error = await response.json();
        alert(error.detail || tx("Erreur lors du déblocage"));
      }
    } catch (error) {
      console.error("Unlock error:", error);
      alert(tx("Erreur de connexion"));
    } finally {
      setUnlocking(false);
    }
  };

  const fetchMessageStatus = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/messages/status/daily`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessageStatus(data);
      }
    } catch (error) {
      console.error("Fetch status error:", error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    // Check if can send message
    if (!isMatch) {
      if (messageStatus.free_messages_remaining <= 0 && messageStatus.credits < 2 && !messageStatus.is_premium) {
        openPaywall("messages");
        return;
      }
    }

    setSending(true);
    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          receiver_id: userId,
          content: newMessage.trim()
        })
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(prev => [...prev, data]);
        setNewMessage("");
        
        // Update message status from server response
        if (data.message_status) {
          if (!isMatch && !messageStatus.is_premium && data.message_status.free_messages_remaining <= 0 && data.message_status.credits < 2) {
            setTimeout(() => openPaywall("messages"), 600);
          }
          setMessageStatus(prev => ({
            ...prev,
            free_messages_remaining: data.message_status.free_messages_remaining,
            credits: data.message_status.credits
          }));
          setUser(prev => ({
            ...prev,
            free_messages_today: data.message_status.free_messages_remaining,
            credits: data.message_status.credits
          }));
        } else if (!isMatch) {
          // Fallback: local update if server doesn't return status
          if (messageStatus.free_messages_remaining > 0) {
            setMessageStatus(prev => ({
              ...prev,
              free_messages_remaining: prev.free_messages_remaining - 1
            }));
            setUser(prev => ({
              ...prev,
              free_messages_today: (prev.free_messages_today || 3) - 1
            }));
          } else {
            setMessageStatus(prev => ({
              ...prev,
              credits: prev.credits - 2
            }));
            setUser(prev => ({
              ...prev,
              credits: (prev.credits || 0) - 2
            }));
          }
        }
      } else if (response.status !== 402) {
        const data = await response.json();
        alert(data.detail || tx("Erreur d'envoi"));
      }
    } catch (error) {
      console.error("Send error:", error);
      alert(tx("Erreur d'envoi du message"));
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateStr) => {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: fr });
    } catch {
      return "";
    }
  };

  // Image handling
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 25 * 1024 * 1024) {
        alert(tx("L'image ne doit pas dépasser 25 Mo"));
        return;
      }
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const sendImageMessage = async () => {
    if (!selectedImage || sending) return;
    if (!messageStatus.is_premium && messageStatus.credits < 2) {
      openPaywall("quota");
      return;
    }
    setSending(true);
    setUploadProgress(0);
    try {
      const blob = await compressImage(selectedImage);
      const fd = new FormData();
      fd.append("receiver_id", userId);
      fd.append("file", blob, "photo.jpg");
      const res = await uploadWithProgress(`${API}/messages/photo`, fd, setUploadProgress);
      if (res.status === 402) {
        openPaywall("quota");
      } else if (res.ok) {
        setMessages(prev => [...prev, res.data]);
        setSelectedImage(null);
        setImagePreview(null);
        if (res.data.message_status) setMessageStatus(prev => ({ ...prev, ...res.data.message_status }));
      } else {
        alert(res.data.detail || tx("Erreur d'envoi de la photo"));
      }
    } finally {
      setSending(false);
      setUploadProgress(null);
    }
  };

  // Voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingDuration(0);
      
      recordingIntervalRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Recording error:", error);
      alert(tx("Impossible d'accéder au microphone"));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(recordingIntervalRef.current);
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    setAudioBlob(null);
    setRecordingDuration(0);
    clearInterval(recordingIntervalRef.current);
  };

  const sendVoiceMessage = async () => {
    if (!audioBlob || sending) return;

    if (!isMatch && !messageStatus.is_premium && messageStatus.free_messages_remaining <= 0 && messageStatus.credits < 2) {
      openPaywall("messages");
      navigate("/credits");
      return;
    }

    setSending(true);
    try {
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      reader.onloadend = async () => {
        const base64Audio = reader.result;
        
        const token = localStorage.getItem("session_token");
        const response = await fetch(`${API}/messages/media`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({
            receiver_id: userId,
            content_type: "voice",
            content: base64Audio,
            duration: recordingDuration
          })
        });

        if (response.ok) {
          const msg = await response.json();
          setMessages(prev => [...prev, msg]);
          setAudioBlob(null);
          setRecordingDuration(0);
          alert(tx("Message vocal envoyé !"));
          
          if (!isMatch && messageStatus.free_messages_remaining > 0) {
            setMessageStatus(prev => ({...prev, free_messages_remaining: prev.free_messages_remaining - 1}));
          }
        } else if (response.status !== 402) {
          const data = await response.json();
          alert(data.detail || tx("Erreur d'envoi"));
        }
        setSending(false);
      };
    } catch (error) {
      console.error("Send voice error:", error);
      alert(tx("Erreur d'envoi du message vocal"));
      setSending(false);
    }
  };

  const playAudio = (audioContent, messageId) => {
    if (playingAudio === messageId) {
      audioRef.current?.pause();
      setPlayingAudio(null);
    } else {
      if (audioRef.current) {
        audioRef.current.src = audioContent;
        audioRef.current.play();
        setPlayingAudio(messageId);
        audioRef.current.onended = () => setPlayingAudio(null);
      }
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="chat-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-surface h-16 flex items-center px-4 gap-3">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate("/matches")}
          className="text-white"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-6 h-6" />
        </Button>
        
        {otherUser && (
          <>
            <Avatar className="w-10 h-10 border border-primary">
              <AvatarImage src={otherUser.picture || otherUser.photos?.[0]} />
              <AvatarFallback className="bg-primary text-white">
                {otherUser.name?.[0]}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1">
              <div className="flex items-center gap-1">
                <h2 className="font-semibold text-white">{otherUser.name}</h2>
                {otherUser.is_verified && (
                  <BadgeCheck className="w-4 h-4 text-green-500" />
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {otherUser.location || (en ? "Online" : "En ligne")}
              </p>
            </div>
          </>
        )}
      </header>

      {/* Message Cost Banner (for non-matches) */}
      {!isMatch && (
        <div className="fixed top-16 left-0 right-0 z-40 bg-secondary/20 border-b border-secondary/30 px-4 py-2">
          <div className="max-w-lg mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-secondary" />
              <span className="text-sm text-secondary">
                {messageStatus.is_premium
                  ? (en ? "Premium: unlimited messages" : "Premium : messages illimités")
                  : messageStatus.free_messages_remaining > 0 
                  ? (en ? `${messageStatus.free_messages_remaining}/3 free messages today` : `${messageStatus.free_messages_remaining}/3 messages gratuits aujourd'hui`)
                  : (en ? "2 credits per message" : "2 crédits par message")
                }
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 text-secondary" />
              <span className="text-sm font-bold text-secondary">{user?.credits || 0}</span>
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className={`flex-1 ${!isMatch ? 'pt-28' : 'pt-20'} pb-20 px-4 overflow-y-auto custom-scrollbar`}>
        <div className="max-w-lg mx-auto space-y-4">
          {/* Match/Start notification */}
          <div className="text-center py-6">
            {isMatch ? (
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/20 rounded-full">
                <Heart className="w-4 h-4 text-primary fill-primary" />
                <span className="text-sm text-primary">{en ? "It's a match!" : "Vous avez matché !"}</span>
              </div>
            ) : (
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/20 rounded-full">
                <Star className="w-4 h-4 text-secondary" />
                <span className="text-sm text-secondary">{tx("Envoyez un message pour attirer son attention")}</span>
              </div>
            )}
          </div>

          {messages.map((msg) => (
            <div
              key={msg.message_id}
              className={`flex ${msg.sender_id === user?.user_id ? "justify-end" : "justify-start"} ${msg.is_system ? "justify-center" : ""}`}
              data-testid={`message-${msg.message_id}`}
            >
              {msg.is_system ? (
                <div className="max-w-[90%] rounded-2xl px-4 py-3 bg-blue-500/20 border border-blue-500/30">
                  <p className="text-sm text-blue-200 whitespace-pre-line">{msg.content}</p>
                </div>
              ) : msg.is_blurred && !isConversationUnlocked ? (
                /* Message flouté des faux membres */
                <div className="relative max-w-[80%]">
                  {/* Message flouté en arrière-plan */}
                  {msg.content_type === "image" ? (
                    <div className="w-52 h-60 rounded-2xl rounded-bl-sm bg-gradient-to-br from-pink-500/40 via-purple-500/30 to-orange-400/40 blur-[2px]" data-testid={`blurred-photo-${msg.message_id}`} />
                  ) : (
                  <div className="bg-card rounded-2xl rounded-bl-sm px-4 py-3 min-h-[120px] min-w-[230px] blur-sm select-none pointer-events-none">
                    <p className="text-white text-sm">
                      {msg.blurred_content || "••••••••••••••••••••••••••..."}
                    </p>
                  </div>
                  )}
                  
                  {/* Overlay avec bouton de déblocage */}
                  <div className="absolute inset-0 bg-black/70 backdrop-blur-sm rounded-2xl flex flex-col items-center justify-center p-3">
                    <Lock className="w-5 h-5 text-white/70 mb-2" />
                    <p className="text-white text-xs text-center mb-2">
                      {msg.content_type === "image" ? "📷 Photo" : "Message"} {en ? "from" : "de"} <span className="font-semibold">{otherUser?.name}</span>
                    </p>
                    
                    <div className="flex flex-col gap-2 w-full">
                      {/* Option avec pièces */}
                      <button
                        onClick={() => (messageStatus.credits < 5 && !messageStatus.is_premium) ? openPaywall("unlock") : handleUnlockConversation()}
                        disabled={unlocking}
                        data-testid={`unlock-message-btn-${msg.message_id}`}
                        className="w-full py-2 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-black font-semibold text-xs rounded-lg flex items-center justify-center gap-1 disabled:opacity-50"
                      >
                        {unlocking ? (
                          <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <Coins className="w-3 h-3" />
                            {en ? "Unlock (5 credits)" : "Débloquer (5 crédits)"}
                          </>
                        )}
                      </button>
                      
                    </div>
                  </div>
                </div>
              ) : msg.content_type === "image" ? (
                <div
                  className={`max-w-[75%] rounded-2xl overflow-hidden ${
                    msg.sender_id === user?.user_id
                      ? "bg-primary rounded-br-sm"
                      : "bg-card rounded-bl-sm"
                  }`}
                >
                  <img 
                    src={msg.content} 
                    alt="Photo" 
                    className="w-full max-h-64 object-cover cursor-pointer"
                    onClick={() => setLightbox(msg.content)}
                    data-testid={`chat-photo-${msg.message_id}`}
                  />
                  <p className={`text-xs px-3 py-2 ${
                    msg.sender_id === user?.user_id ? "text-white/60" : "text-muted-foreground"
                  }`}>
                    {formatTime(msg.created_at)}
                  </p>
                </div>
              ) : msg.content_type === "voice" ? (
                <div
                  className={`max-w-[75%] rounded-2xl px-4 py-3 ${
                    msg.sender_id === user?.user_id
                      ? "bg-primary text-white rounded-br-sm"
                      : "bg-card text-white rounded-bl-sm"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => playAudio(msg.content, msg.message_id)}
                      className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"
                    >
                      {playingAudio === msg.message_id ? (
                        <Pause className="w-5 h-5" />
                      ) : (
                        <Play className="w-5 h-5" />
                      )}
                    </button>
                    <div className="flex-1">
                      <div className="h-1 bg-white/30 rounded-full">
                        <div className="h-full bg-white rounded-full" style={{width: playingAudio === msg.message_id ? '50%' : '0%'}} />
                      </div>
                      <p className="text-xs mt-1">{msg.duration ? formatDuration(msg.duration) : "0:00"}</p>
                    </div>
                  </div>
                  <p className={`text-xs mt-2 ${
                    msg.sender_id === user?.user_id ? "text-white/60" : "text-muted-foreground"
                  }`}>
                    {formatTime(msg.created_at)}
                  </p>
                </div>
              ) : (
                <div
                  className={`max-w-[75%] rounded-2xl px-4 py-3 ${
                    msg.sender_id === user?.user_id
                      ? "bg-primary text-white rounded-br-sm"
                      : "bg-card text-white rounded-bl-sm"
                  }`}
                >
                  <p>{msg.content}</p>
                  <p className={`text-xs mt-1 ${
                    msg.sender_id === user?.user_id ? "text-white/60" : "text-muted-foreground"
                  }`}>
                    {formatTime(msg.created_at)}
                  </p>
                </div>
              )}
            </div>
          ))}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Hidden audio element for playback */}
      <audio ref={audioRef} className="hidden" />
      
      {/* Hidden file input for images */}
      <input
        type="file"
        ref={fileInputRef}
        accept="image/*"
        className="hidden"
        onChange={(e) => { handleImageSelect(e); e.target.value = ""; }}
        data-testid="chat-photo-input"
      />

      {lightbox && (
        <div className="fixed inset-0 z-[150] bg-black/95 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="chat-photo-lightbox">
          <img src={lightbox} alt="Photo" className="max-w-full max-h-full object-contain rounded-lg" />
          <button className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center" style={{ top: "max(1rem, env(safe-area-inset-top))" }} data-testid="chat-photo-lightbox-close">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
      )}

      {/* Image Preview */}
      {imagePreview && (
        <div className="fixed bottom-20 left-0 right-0 p-4 glass-surface">
          <div className="max-w-lg mx-auto relative">
            <img src={imagePreview} alt="Preview" className="w-32 h-32 object-cover rounded-xl" data-testid="chat-photo-preview" />
            <button
              onClick={() => { setSelectedImage(null); setImagePreview(null); }}
              className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center"
            >
              <X className="w-4 h-4 text-white" />
            </button>
            <Button
              onClick={sendImageMessage}
              disabled={sending}
              data-testid="chat-send-photo-btn"
              className="mt-3 w-full bg-primary hover:bg-primary/90 rounded-full"
            >
              {sending ? `${en ? "Sending…" : "Envoi…"} ${uploadProgress ?? 0}%` : (messageStatus.is_premium ? (en ? "Send photo (free with Premium)" : "Envoyer la photo (gratuit Premium)") : (en ? "Send photo (2 credits)" : "Envoyer la photo (2 crédits)"))}
            </Button>
          </div>
        </div>
      )}

      {/* Voice Recording UI */}
      {(isRecording || audioBlob) && (
        <div className="fixed bottom-20 left-0 right-0 p-4 glass-surface">
          <div className="max-w-lg mx-auto flex items-center gap-4">
            {isRecording ? (
              <>
                <div className="flex items-center gap-2 flex-1">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-white">{tx("Enregistrement...")} {formatDuration(recordingDuration)}</span>
                </div>
                <Button variant="outline" size="icon" onClick={cancelRecording} className="rounded-full">
                  <X className="w-5 h-5" />
                </Button>
                <Button onClick={stopRecording} className="bg-primary rounded-full">
                  <MicOff className="w-5 h-5 mr-2" />
                  {tx("Terminer")}
                </Button>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 flex-1">
                  <Mic className="w-5 h-5 text-primary" />
                  <span className="text-white">{tx("Message vocal (")}{formatDuration(recordingDuration)})</span>
                </div>
                <Button variant="outline" size="icon" onClick={cancelRecording} className="rounded-full">
                  <X className="w-5 h-5" />
                </Button>
                <Button onClick={sendVoiceMessage} disabled={sending} className="bg-primary rounded-full">
                  <Send className="w-5 h-5 mr-2" />
                  {sending ? (en ? "Sending..." : "Envoi...") : (en ? "Send" : "Envoyer")}
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Input */}
      <form 
        onSubmit={sendMessage}
        className="fixed bottom-0 left-0 right-0 glass-surface p-4"
      >
        <div className="max-w-lg mx-auto flex gap-2">
          {/* Photo Button */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => fileInputRef.current?.click()}
            className="w-12 h-12 rounded-full hover:bg-white/10"
            data-testid="photo-btn"
          >
            <Image className="w-5 h-5 text-muted-foreground" />
          </Button>
          
          {/* Voice Button */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={startRecording}
            disabled={isRecording}
            className="w-12 h-12 rounded-full hover:bg-white/10"
            data-testid="voice-btn"
          >
            <Mic className="w-5 h-5 text-muted-foreground" />
          </Button>
          
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={isMatch ? (en ? "Write a message..." : "Écrivez un message...") : `Message (${messageStatus.is_premium || messageStatus.free_messages_remaining > 0 ? (en ? 'free' : 'gratuit') : (en ? '2 credits' : '2 crédits')})...`}
            className="flex-1 h-12 rounded-full bg-muted/50 border-transparent focus:border-primary"
            data-testid="message-input"
          />
          <Button
            type="submit"
            size="icon"
            className="w-12 h-12 rounded-full bg-primary hover:bg-primary/90"
            disabled={!newMessage.trim() || sending}
            data-testid="send-btn"
          >
            <Send className="w-5 h-5" />
          </Button>
        </div>
      </form>
    </div>
  );
}
